import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import SegmentMixin from '~/mixins/Segment/Segment'

import { useInfluencersInteractionsStore } from '~/stores/influencersInteractions'
import { useTagStore } from '~/stores/tag'
import { useUserBandStore } from '~/stores/userBand'
import { useUserFavoritesStore } from '~/stores/userFavorites'

import type { Influencer, StatsV3Influencer } from '~/types/influencer'
import type { StoreInfluencerInteraction } from '~/types/influencerInteraction'
import type {
  InfluencerTrackingInfo,
  OriginPageName,
} from '~/types/Segment/SegmentEventLibrary/SegmentEventLibraryBucketList'

/**
 * @deprecated Use composables/useSegmentBucketList.ts
 */
export default defineComponent({
  mixins: [SegmentMixin],
  computed: {
    ...mapState(useUserBandStore, ['BAND_TRACKING_INFO']),
    ...mapState(useInfluencersInteractionsStore, [
      'GET_INTERACTION_FROM_INFLUENCER_ID',
    ]),
    ...mapState(useUserFavoritesStore, ['ORIGIN_PAGE', 'CAMPAIGN_ID']),
  },
  methods: {
    ...mapActions(useTagStore, ['GET_TAG_FROM_ID']),
    ...mapActions(useUserFavoritesStore, [
      'SET_ORIGIN_PAGE',
      'SET_CAMPAIGN_ID',
    ]),
    /**
     * @deprecated Use composables/useSegmentBucketList.ts
     */
    computeInfluencerInteraction(
      influencerId: number,
    ): null | StoreInfluencerInteraction {
      return this.GET_INTERACTION_FROM_INFLUENCER_ID(influencerId) ?? null
    },
    /**
     * @deprecated Use composables/useSegmentBucketList.ts
     */
    getInfluencerTrackingInfo(
      influencers: (Influencer | StatsV3Influencer)[] | null,
    ): InfluencerTrackingInfo {
      const curator_badges_list = new Set<string>()
      const curator_countries_list = new Set<string>()
      const curator_ids_list = new Set<number>()
      const curator_names_list = new Set<string>()
      const curator_music_genres_list = new Set<string>()
      const curator_types_list = new Set<string>()
      let num_tracks_sent = 0

      if (influencers && influencers.length) {
        const alreadyAddedInfluencers: { [infId: number]: boolean } = {}

        influencers.forEach((influencer: Influencer | StatsV3Influencer) => {
          const influencer_badge =
            influencer.tags.identity.influencer_badge || []
          influencer_badge
            .map((tagID: number) => this.GET_TAG_FROM_ID(tagID)?.name as string)
            .forEach(curator_badges_list.add, curator_badges_list)

          const country = influencer.tags.identity.country || []
          country
            .map((tagID: number) => this.GET_TAG_FROM_ID(tagID)?.name as string)
            .forEach(curator_countries_list.add, curator_countries_list)

          const influencer_kind = influencer.tags.identity.influencer_kind || []
          influencer_kind
            .map((tagID: number) => this.GET_TAG_FROM_ID(tagID)?.name as string)
            .forEach(curator_types_list.add, curator_types_list)

          const subgenre = influencer.tags.liked?.subgenre || []
          subgenre
            .map((tagID: number) => this.GET_TAG_FROM_ID(tagID)?.name as string)
            .forEach(curator_music_genres_list.add, curator_music_genres_list)

          curator_ids_list.add(influencer.id)
          curator_names_list.add(influencer.entity)

          if (!alreadyAddedInfluencers[influencer.id]) {
            num_tracks_sent +=
              this.computeInfluencerInteraction(influencer.id)?.total ?? 0
            alreadyAddedInfluencers[influencer.id] = true
          }
        })
      }
      return {
        curator_badges_list: [...curator_badges_list],
        curator_countries_list: [...curator_countries_list],
        curator_ids_list: [...curator_ids_list],
        curator_names_list: [...curator_names_list],
        curator_music_genres_list: [...curator_music_genres_list],
        curator_types_list: [...curator_types_list],
        num_tracks_sent,
      }
    },
    /**
     * @deprecated Use composables/useSegmentBucketList.ts
     */
    trackListCreationInitiated(): void {
      this.trackSegmentEvent('List - List Creation Initiated', {
        ...this.BAND_TRACKING_INFO,
        id_campaign: this.CAMPAIGN_ID,
        curator_saved_from: this.ORIGIN_PAGE as OriginPageName,
        list_created_from: this.ORIGIN_PAGE as OriginPageName,
      })
    },
    /**
     * @deprecated Use composables/useSegmentBucketList.ts
     */
    trackListCreationFinalized(bucketName: string): void {
      this.trackSegmentEvent('List - List Creation Finalized', {
        ...this.BAND_TRACKING_INFO,
        id_campaign: this.CAMPAIGN_ID,
        list_name: bucketName,
        curator_saved_from: this.ORIGIN_PAGE as OriginPageName,
        list_created_from: this.ORIGIN_PAGE as OriginPageName,
      })
    },
    /**
     * @deprecated Use composables/useSegmentBucketList.ts
     */
    trackListSaveInitiated(
      influencers: (Influencer | StatsV3Influencer)[] | null,
    ): any {
      const details = {
        ...this.BAND_TRACKING_INFO,
        ...this.getInfluencerTrackingInfo(influencers),
        id_campaign: this.CAMPAIGN_ID,
        curator_saved_from: this.ORIGIN_PAGE as OriginPageName,
        // TODO: fix this - it's missing "num_curators_saved_to_list"
      }
      this.trackSegmentEvent('List - Save Initiated', details)
      return details // temporary hack for existing events of legacy system
    },
    /**
     * @deprecated Use composables/useSegmentBucketList.ts
     */
    trackListSaveFinalized(
      influencers: (Influencer | StatsV3Influencer)[] | null,
      num_curators_in_list: number,
      num_curators_saved_to_list: number,
      list_name: string,
    ): any {
      const details = {
        ...this.BAND_TRACKING_INFO,
        ...this.getInfluencerTrackingInfo(influencers),
        id_campaign: this.CAMPAIGN_ID,
        curator_saved_from: this.ORIGIN_PAGE as OriginPageName,
        num_curators_in_list,
        num_curators_saved_to_list,
        page_title: this.$route.name,
        list_name,
      }
      this.trackSegmentEvent('List - Save Finalized', details)
      return details // temporary hack for existing events of legacy system
    },
  },
})
